import Alert from '@mui/material/Alert';
import styles from './styles.module.scss';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { styled } from '@mui/material/styles';
import { SubscriptionNotificationTier } from '@/hooks/useSubscriptionNotification';

/** Soft, "corporate" light-tint palette (pale background, muted colored text/icon) — WCAG AA checked. */
const TIER_COLORS: Record<SubscriptionNotificationTier, { bg: string; text: string }> = {
  reminder: { bg: '#EAF1F8', text: '#3D6690' },
  critical: { bg: '#FBF1E3', text: '#9C5B18' },
  expired: { bg: '#FBEAE9', text: '#B33F3A' },
};

/** Maps our 3-tier subscription severity onto MUI's Alert `severity` prop, purely for the icon/a11y semantics. */
const TIER_SEVERITY: Record<SubscriptionNotificationTier, 'info' | 'warning' | 'error'> = {
  reminder: 'info',
  critical: 'warning',
  expired: 'error',
};

const StyledAlert = styled(Alert)<{ ownerState: { tier: SubscriptionNotificationTier } }>(({ ownerState }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .MuiAlert-action': {
    marginLeft: 0,
  },
  backgroundColor: TIER_COLORS[ownerState.tier].bg,
  '& .MuiSvgIcon-root': {
    fill: TIER_COLORS[ownerState.tier].text,
  },
  '& .MuiSvgIcon-root>path': {
    scale: '.95!important',
  },
}));

interface Props {
  endDays: number;
  tier: SubscriptionNotificationTier;
}

const HeadAlert: React.FC<Props> = ({ endDays, tier }) => {
  const { t } = useTranslation();

  return (
    <StyledAlert
      className={clsx('flex items-center mb-6', styles[tier])}
      icon={<WarningAmberRoundedIcon className='!w-[32px] !h-[34px]' />}
      severity={TIER_SEVERITY[tier]}
      ownerState={{ tier }}
    >
      <p className={clsx('subtitle2s m-0', styles[tier])}>
        {tier === 'expired' ? (
          <>
            {t('notification.subscriptionExpired')}{' '}
            <Link to='/profile?tab=subscriptionManagement' className={styles.renewLink}>
              {t('notification.renewNow')}
            </Link>
          </>
        ) : (
          t('notification.expiredInXdays', { num: endDays })
        )}
      </p>
    </StyledAlert>
  );
};

export default HeadAlert;
